import { useCallback } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { useAdminClaim } from '../../hooks/use-admin-claim';
import { useCases } from '../../hooks/use-cases';
import { useEventPreview } from '../../hooks/use-event-preview';
import { useEventTypeCounts } from '../../hooks/use-event-type-counts';
import { useRefreshEventsOnImport } from '../../hooks/use-refresh-events-on-import';
import { useCaseRoomPageState } from './use-case-room-page-state';
import { useCaseRoomSearchFocus } from './use-case-room-search-focus';
import { useCaseTimeline } from './use-case-timeline';
import { useSyncCaseMenuActions } from './use-sync-case-menu-actions';

export function useCaseRoomController() {
  const { caseId } = useParams({ strict: false }) as { caseId: string };
  const { event: eventFromSearch } = useSearch({ strict: false }) as { event?: string };
  const { data: cases } = useCases();
  const { data: isAdmin } = useAdminClaim();
  const pageState = useCaseRoomPageState(caseId);
  const focus = useCaseRoomSearchFocus(caseId, eventFromSearch, pageState.openEvent);

  const openSources = useCallback(
    () => pageState.setIsSourcesOpen(true),
    [pageState.setIsSourcesOpen],
  );
  const openMembers = useCallback(
    () => pageState.setIsMembersOpen(true),
    [pageState.setIsMembersOpen],
  );

  const timeline = useCaseTimeline(
    caseId,
    pageState.filters,
    pageState.highlightedEventIds,
    focus.focusEvent,
  );
  const { data: eventCounts } = useEventTypeCounts(caseId);
  const shouldLoadPreview =
    timeline.isSearchMode &&
    pageState.selectedEvent !== null &&
    Object.keys(pageState.selectedEvent.metadata).length === 0;
  const previewEvent = useEventPreview(caseId, pageState.selectedEvent, shouldLoadPreview);
  useRefreshEventsOnImport(caseId);

  const caseItem = cases?.find((item) => item.id === caseId);
  useSyncCaseMenuActions({
    caseId,
    saksnummer: caseItem?.saksnummer ?? caseId,
    filters: pageState.filters,
    onOpenSources: openSources,
    onOpenMembers: openMembers,
  });

  return {
    caseId,
    caseItem,
    pageState,
    focus,
    timeline,
    eventCounts,
    previewEvent,
    openSources,
    canInvite: pageState.caseRole === 'owner' || isAdmin === true,
  };
}
